import {
  useCreateAssetType,
  useUpdateAssetType,
} from "@features/ax/settings/assetClassification/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/ui/dialog";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useState } from "react";

interface AssetClassificationFormProps {
  editData?: any;
  trigger: React.ReactNode;
  code: string;
}

export const AssetTypeCreate: React.FC<AssetClassificationFormProps> = ({
  editData,
  trigger,
  code,
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const isEditMode = Boolean(editData);
  const { mutateAsync: createAssetType, isPending: isCreating } =
    useCreateAssetType();
  const { mutateAsync: updateAssetType, isPending: isUpdating } =
    useUpdateAssetType();

  const [formData, setFormData] = useState({
    name: editData?.name || "",
    description: editData?.description || "",
    code: editData?.code || "",
  });

  // Reset form data when dialog opens/closes or editData changes
  useEffect(() => {
    if (open) {
      setFormData({
        name: editData?.name || "",
        description: editData?.description || "",
        code: editData?.code || "",
      });
    }
  }, [open, editData]);

  const getSubmitButtonText = () => {
    return isEditMode ? "Update Asset Type" : "Add Asset Type";
  };

  // Form field definitions
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Asset Type Name",
      placeholder: "Enter asset type name...",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter asset type description...",
      rows: 3,
      className: "col-span-1 mt-3",
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  // Reset form when formData changes
  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  // Handle form submission
  const handleSubmit = (data: any) => {
    // Validate required fields
    if (!data.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter a name",
        variant: "destructive",
      });
      return;
    }

    if (isEditMode) {
      updateAssetType({
        code: code,
        name: data.name,
        remote_utility_id: getRemoteUtilityId(),
        description: data.description,
      });
    } else {
      createAssetType({
        code: code + "_" + data.name,
        name: data.name,
        remote_utility_id: getRemoteUtilityId(),
        description: data.description,
      });
    }

    handleClose();
  };

  // Handle form reset
  const handleReset = () => {
    const resetData = {
      name: "",
      description: "",
      code: "",
    };
    setFormData(resetData);
    form.reset(resetData);
  };

  // Handle dialog close
  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} key={"add-type"}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Asset Type</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            key={`asset-type-form-${editData?.code || "new"}-${code}`}
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            gridColumns={1}
            responsiveColumns={{
              sm: 1,
              md: 1,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: getSubmitButtonText(),
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6 p-0",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-sm text-gray-500 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
